import React, { useRef, useState, useEffect } from 'react';
import EmojiPicker from 'emoji-picker-react';

interface EmojiPickerInputProps {
    value: string;
    onChange: (value: string) => void;
    inputRef?: React.RefObject<HTMLInputElement | HTMLTextAreaElement>;
    buttonContent?: React.ReactNode;
    pickerPosition?: 'bottom-full' | 'bottom-0';
    className?: string;
}

const EmojiPickerInput: React.FC<EmojiPickerInputProps> = ({
    value,
    onChange,
    inputRef,
    buttonContent,
    pickerPosition = 'bottom-full',
    className = '',
}) => {
    const [showEmojiPicker, setShowEmojiPicker] = useState(false);
    const [cursorPosition, setCursorPosition] = useState(0);
    const emojiPickerRef = useRef<HTMLDivElement>(null);
    const buttonRef = useRef<HTMLButtonElement>(null);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (
                emojiPickerRef.current &&
                !emojiPickerRef.current.contains(event.target as Node) &&
                !buttonRef.current?.contains(event.target as Node)
            ) {
                setShowEmojiPicker(false);
            }
        };

        if (showEmojiPicker) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [showEmojiPicker]);

    // Ajouter des event listeners pour tracker la position du curseur
    useEffect(() => {
        const input = inputRef?.current;
        if (!input) return;

        const updateCursorPosition = () => {
            setCursorPosition(input.selectionStart || 0);
        };

        input.addEventListener('click', updateCursorPosition);
        input.addEventListener('keyup', updateCursorPosition);
        input.addEventListener('focus', updateCursorPosition);
        input.addEventListener('select', updateCursorPosition);

        return () => {
            input.removeEventListener('click', updateCursorPosition);
            input.removeEventListener('keyup', updateCursorPosition);
            input.removeEventListener('focus', updateCursorPosition);
            input.removeEventListener('select', updateCursorPosition);
        };
    }, [inputRef]);

    const toggleEmojiPicker = () => setShowEmojiPicker(!showEmojiPicker);

    const onEmojiClick = (emojiObject: any) => {
        const emoji = emojiObject.emoji;
        if (emoji) {
            const newText = value.slice(0, cursorPosition) + emoji + value.slice(cursorPosition);
            onChange(newText);

            setTimeout(() => {
                if (inputRef?.current) {
                    const newPosition = cursorPosition + emoji.length;
                    inputRef.current.selectionStart = newPosition;
                    inputRef.current.selectionEnd = newPosition;
                    inputRef.current.focus();
                }
            }, 0);
            setShowEmojiPicker(false);
        }
    };

    return (
        <div className={`relative ${className}`}>
            <button
                ref={buttonRef}
                type="button"
                onClick={toggleEmojiPicker}
                className="flex justify-center items-center hover:opacity-80 transition-opacity"
            >
                {buttonContent || (
                    <div className="rounded-[65px] flex justify-center items-center gap-1">
                        <div className="size-4 relative overflow-hidden flex items-center justify-center">
                            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                                <path
                                    d="M8 14A6 6 0 1 0 8 2a6 6 0 0 0 0 12Z"
                                    stroke="#525254"
                                    strokeWidth="1.5"
                                />
                                <circle cx="6" cy="7" r="0.5" fill="#525254" />
                                <circle cx="10" cy="7" r="0.5" fill="#525254" />
                                <path
                                    d="M5.5 10c.5.5 1.5 1 2.5 1s2-.5 2.5-1"
                                    stroke="#525254"
                                    strokeWidth="1.5"
                                    strokeLinecap="round"
                                />
                            </svg>
                        </div>
                        <div className="justify-center text-[#525254] text-[13px] font-semibold font-['Montserrat'] leading-none">
                            Emoji
                        </div>
                    </div>
                )}
            </button>
            {showEmojiPicker && (
                <div
                    ref={emojiPickerRef}
                    className={`absolute ${pickerPosition} right-0 mb-2 z-[100]`}
                >
                    <EmojiPicker onEmojiClick={onEmojiClick} />
                </div>
            )}
        </div>
    );
};

export default EmojiPickerInput;
